/* Panel shell.
 *
 * Every region of the terminal is a panel: same chrome, same header, same
 * affordances. Specification section 50 requires each to be configurable, so
 * they are uniform containers rather than bespoke layouts.
 */
import type { ReactNode } from 'react'

export function Panel({
  title, actions, children, noPadding, footer,
}: {
  title: string
  actions?: ReactNode
  children: ReactNode
  noPadding?: boolean
  footer?: ReactNode
}) {
  return (
    <section
      style={{
        // height:100% matters. A Panel sits inside a grid cell that stretches,
        // but the section itself is a block child and would otherwise take only
        // its content height — leaving the footer riding up under the header
        // and a large dead area beneath the panel. That is what made the chart
        // look empty with its indicator toolbar stuck near the top.
        display: 'flex', flexDirection: 'column',
        height: '100%', minHeight: 0, minWidth: 0,
        background: 'var(--bg-panel)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        overflow: 'hidden',
      }}
    >
      <header
        style={{
          // The reference centres a card's caption; when a panel carries
          // actions the caption keeps left so the two never collide in a
          // narrow column.
          display: 'flex', alignItems: 'center',
          justifyContent: actions ? 'space-between' : 'center',
          gap: 'var(--sp-2)',
          padding: '0 var(--sp-3)',
          height: 32, flexShrink: 0,
          borderBottom: '1px solid var(--border)',
          background: 'var(--accent-soft)',
        }}
      >
        <h2
          style={{
            fontSize: 'var(--fs-2xs)', fontWeight: 800, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'var(--accent)',
          }}
        >
          {title}
        </h2>
        {actions && (
          <div style={{ display: 'flex', gap: 'var(--sp-1)', alignItems: 'center' }}>
            {actions}
          </div>
        )}
      </header>

      <div
        style={{
          flex: 1, minHeight: 0, overflow: 'auto',
          padding: noPadding ? 0 : 'var(--sp-3)',
        }}
      >
        {children}
      </div>

      {footer && (
        <footer
          style={{
            flexShrink: 0, padding: 'var(--sp-2) var(--sp-3)',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
            fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
          }}
        >
          {footer}
        </footer>
      )}
    </section>
  )
}
